import { EquipSlot, SpriteData, SpritesheetSpriteData } from '@common/components/components';
import { HAND_SLOTS } from '@common/data/BodyTypeSlots';
import { Container, Sprite, Texture } from 'pixi.js';

import { AssetManager } from '../AssetManager';
import { ClientWorld } from '../ClientWorld';
import { RgbChannelSwapFilter } from '../filters/RgbChannelSwapFilter';
import { SpritesheetService } from './SpritesheetService';

/**
 * Service for reconciling PIXI containers with ECS entity state.
 * Encapsulates the logic for rendering an entity and its equipment.
 * World-agnostic, allowing use with both the main game world and mock worlds.
 */
export class RepresentationService {
  private spritesheetService: SpritesheetService;

  constructor(spritesheetService: SpritesheetService) {
    this.spritesheetService = spritesheetService;
  }

  /**
   * Reconciles a PIXI container with the state of an entity in a world.
   * Ensures the container matches the visual look of the entity (sprite + equipment).
   *
   * @param container The PIXI Container to update
   * @param world The ECS world (ClientWorld) containing the entity
   * @param entityId The ID of the entity to represent
   */
  public reconcile(container: Container, world: ClientWorld, entityId: number): void {
    const spriteComp = world.getComponent(entityId, 'Sprite');
    const spritesheetSprite = world.getComponent(entityId, 'SpritesheetSprite');

    if (!spriteComp) {
      container.visible = false;
      return;
    }

    container.visible = spriteComp.visible;

    // 1. Root Sprite
    let mainSprite = container.getChildByLabel('main') as Sprite;
    if (!mainSprite) {
      mainSprite = new Sprite(Texture.EMPTY);
      mainSprite.label = 'main';
      container.addChild(mainSprite);
    }

    this.updateSprite(mainSprite, world, entityId, spriteComp, spritesheetSprite ?? undefined);

    // 2. Face Decorations
    const faceDecoration = world.getComponent(entityId, 'FaceDecoration');
    const animation = world.getComponent(entityId, 'Animation');
    if (faceDecoration && spritesheetSprite && animation) {
      const eyesSprite = this.getOrCreateDecorationSprite(container, 'eyes');
      const mouthSprite = this.getOrCreateDecorationSprite(container, 'mouth');

      this.updateDecorationSprite(
        eyesSprite,
        world,
        entityId,
        faceDecoration.eyesStyle,
        faceDecoration.eyePrimaryColour,
        faceDecoration.eyeSecondaryColour,
        spritesheetSprite.spritesheetId,
        'EyePoint',
        animation.name,
        spritesheetSprite.frameIndex,
      );

      this.updateDecorationSprite(
        mouthSprite,
        world,
        entityId,
        faceDecoration.mouthStyle,
        faceDecoration.mouthPrimaryColour,
        faceDecoration.mouthSecondaryColour,
        spritesheetSprite.spritesheetId,
        'MouthPoint',
        animation.name,
        spritesheetSprite.frameIndex,
      );
    } else {
      // Hide decorations if components are missing
      const eyes = container.getChildByLabel('eyes');
      if (eyes) eyes.visible = false;
      const mouth = container.getChildByLabel('mouth');
      if (mouth) mouth.visible = false;
    }

    // 3. Equipment
    const equipment = world.getComponent(entityId, 'Equipment');
    if (equipment) {
      for (const slot of HAND_SLOTS) {
        const itemId = equipment.slots[slot];
        const slotName = `slot_${slot}`;
        let itemSprite = container.getChildByLabel(slotName) as Sprite;

        if (itemId !== null && itemId !== undefined) {
          const itemSpritesheet = world.getComponent(itemId, 'SpritesheetSprite');
          if (itemSpritesheet) {
            if (!itemSprite) {
              itemSprite = new Sprite(Texture.EMPTY);
              itemSprite.label = slotName;
              container.addChildAt(itemSprite, 1); // Render equipment on top of main sprite
            }

            this.updateItemSprite(itemSprite, world, entityId, slot, itemSpritesheet);
            itemSprite.visible = true;
          } else if (itemSprite) {
            itemSprite.visible = false;
          }
        } else if (itemSprite) {
          itemSprite.visible = false;
        }
      }
    } else {
      // Hide all equipment slots if no equipment component
      for (const slot of HAND_SLOTS) {
        const itemSprite = container.getChildByLabel(`slot_${slot}`);
        if (itemSprite) {
          itemSprite.visible = false;
        }
      }
    }

    // 4. Sorting
    container.sortableChildren = true;
    mainSprite.zIndex = spriteComp.zIndexOffset || 0;

    const eyes = container.getChildByLabel('eyes');
    const mouth = container.getChildByLabel('mouth');
    if (eyes) eyes.zIndex = mainSprite.zIndex + 0.1;
    if (mouth) mouth.zIndex = mainSprite.zIndex + 0.1;

    for (const slot of HAND_SLOTS) {
      const itemSprite = container.getChildByLabel(`slot_${slot}`) as Sprite;
      if (itemSprite && itemSprite.visible) {
        itemSprite.zIndex = (mainSprite.zIndex || 0) + 1;
      }
    }
  }

  private getOrCreateDecorationSprite(container: Container, label: string): Sprite {
    let sprite = container.getChildByLabel(label) as Sprite;
    if (!sprite) {
      sprite = new Sprite(Texture.EMPTY);
      sprite.label = label;
      sprite.filters = [new RgbChannelSwapFilter()];
      container.addChild(sprite);
    }
    return sprite;
  }

  /**
   * Updates a Sprite instance with texture and transform from ECS components.
   */
  private updateSprite(
    sprite: Sprite,
    world: ClientWorld,
    entityId: number,
    spriteComp: SpriteData,
    spritesheetSprite?: SpritesheetSpriteData,
  ): void {
    // Texture update
    let textureId = spriteComp.textureId;
    if (spritesheetSprite) {
      textureId = `${spritesheetSprite.spritesheetId}_${spritesheetSprite.frameIndex}`;
    }

    if (textureId) {
      const assetManager = AssetManager.getInstance();
      if (assetManager.hasTexture(textureId)) {
        sprite.texture = assetManager.getTexture(textureId);
      }
    }
    // If no texture found, we stay with current or Texture.EMPTY (initialized in reconcile)

    sprite.anchor.set(spriteComp.anchorX, spriteComp.anchorY);

    // Apply SkinColour component if present
    const skinColour = world.getComponent(entityId, 'SkinColour');
    if (skinColour) {
      sprite.tint = skinColour.colour;
    } else {
      sprite.tint = spriteComp.tint;
    }

    // Transform modifiers (bounce/sway effects)
    const animOffset = world.getComponent(entityId, 'AnimationTransformOffset');
    if (animOffset) {
      sprite.position.set(animOffset.x, animOffset.y);
      sprite.rotation = animOffset.rotation;
      sprite.scale.set(animOffset.scaleX, animOffset.scaleY);
    } else {
      sprite.position.set(0, 0);
      sprite.rotation = 0;
      sprite.scale.set(1, 1);
    }
  }

  /**
   * Updates a decoration sprite (eyes or mouth).
   */
  private updateDecorationSprite(
    sprite: Sprite,
    world: ClientWorld,
    entityId: number,
    decoStyle: string,
    primaryColour: number,
    secondaryColour: number,
    bodySheetId: string,
    pointType: string,
    animName: string,
    bodyFrameIndex: number,
  ): void {
    const filter = sprite.filters?.[0] as RgbChannelSwapFilter | undefined;
    if (filter) {
      filter.primaryColour = primaryColour;
      filter.secondaryColour = secondaryColour;
    }

    // 1. Resolve which frame of the decoration sheet to show.
    const decorationFrame = this.resolveDecorationFrame(
      decoStyle,
      bodySheetId,
      animName,
      bodyFrameIndex,
    );

    if (decorationFrame === null) {
      sprite.visible = false;
      return;
    }

    const textureId = this.spritesheetService.getTextureId(decoStyle, decorationFrame);
    const assetManager = AssetManager.getInstance();

    if (assetManager.hasTexture(textureId)) {
      sprite.texture = assetManager.getTexture(textureId);
      sprite.visible = true;

      // 2. Position using point-to-point alignment (paper-doll style)
      this.alignDecorationToPoint(
        sprite,
        world,
        entityId,
        decoStyle,
        bodySheetId,
        pointType,
        decorationFrame,
        bodyFrameIndex,
      );
    } else {
      sprite.visible = false;
    }
  }

  /**
   * Determines the correct frame of a decoration spritesheet to display,
   * accounting for fallback animations and varying frame counts.
   */
  private resolveDecorationFrame(
    decoStyle: string,
    bodySheetId: string,
    animName: string,
    bodyFrameIndex: number,
  ): number | null {
    const sService = this.spritesheetService;

    const bodyAnimation = sService.getAnimation(bodySheetId, animName);
    if (!bodyAnimation) return null;

    // Resolve target animation for decoration, with fallback for walking states
    let targetAnimName = animName;
    if (!sService.hasAnimation(decoStyle, targetAnimName)) {
      if (animName.startsWith('walk_')) {
        const fallback = animName.replace('walk_', 'idle_');
        if (sService.hasAnimation(decoStyle, fallback)) {
          targetAnimName = fallback;
        } else {
          return null;
        }
      } else {
        return null;
      }
    }

    const decoAnimation = sService.getAnimation(decoStyle, targetAnimName);
    if (!decoAnimation) return null;

    // Calculate relative progress in body animation (1-indexed)
    const relativeFrame = bodyFrameIndex - bodyAnimation.fromFrame + 1;

    // Robust Frame Wrapping:
    // If the body has more frames than the decoration fallback (e.g. 4 vs 2),
    // we use a modulo to loop the decoration animation within the body's duration.
    // This prevents "bleeding" into adjacent animation tags in the spritesheet grid.
    const decoRelativeFrame = ((relativeFrame - 1) % decoAnimation.frameCount) + 1;

    return sService.animationFrameToSheetFrame(decoStyle, targetAnimName, decoRelativeFrame);
  }

  /**
   * Aligns the decoration sprite offset based on both body and decoration attachment points.
   */
  private alignDecorationToPoint(
    sprite: Sprite,
    world: ClientWorld,
    entityId: number,
    decoStyle: string,
    bodySheetId: string,
    pointType: string,
    decoFrameIndex: number,
    bodyFrameIndex: number,
  ): void {
    const sService = this.spritesheetService;
    const bodyPoint = sService.getPointForFrame(bodySheetId, pointType, bodyFrameIndex);
    const decoPoint = sService.getPointForFrame(decoStyle, pointType, decoFrameIndex);
    const bodySize = sService.getFrameSize(bodySheetId);
    const decoSize = sService.getFrameSize(decoStyle);

    if (bodySize && decoSize && bodyPoint && decoPoint) {
      // Set anchor to the RELATIVE position of the point within the decoration frame.
      // E.g. if the eye center is at (7,7) in a 16x16 frame, anchor is (0.4375, 0.4375).
      sprite.anchor.set(decoPoint.x / decoSize.width, decoPoint.y / decoSize.height);

      // Set position to the RELATIVE position of the body point within the entity container.
      // Body sprites are anchored at (0.5, 1) by default.
      sprite.x = -bodySize.width / 2 + bodyPoint.x;
      sprite.y = -bodySize.height + bodyPoint.y;
    } else if (bodySize && bodyPoint) {
      // Fallback if decoration doesn't have its own point data
      sprite.anchor.set(0.5, 0.5);
      sprite.x = -bodySize.width / 2 + bodyPoint.x;
      sprite.y = -bodySize.height + bodyPoint.y;
    }

    // Inherit transform offsets (bounce/sway) from body animation
    const animOffset = world.getComponent(entityId, 'AnimationTransformOffset');
    if (animOffset) {
      sprite.x += animOffset.x;
      sprite.y += animOffset.y;
      sprite.rotation = animOffset.rotation;
      sprite.scale.set(animOffset.scaleX, animOffset.scaleY);
    } else {
      sprite.rotation = 0;
      sprite.scale.set(1, 1);
    }
  }

  /**
   * Updates an equipment item Sprite.
   */
  private updateItemSprite(
    itemSprite: Sprite,
    world: ClientWorld,
    ownerId: number,
    slot: EquipSlot,
    itemSpritesheet: SpritesheetSpriteData,
  ): void {
    const textureId = `${itemSpritesheet.spritesheetId}_${itemSpritesheet.frameIndex}`;
    const assetManager = AssetManager.getInstance();
    if (assetManager.hasTexture(textureId)) {
      itemSprite.texture = assetManager.getTexture(textureId);
    }

    // Centered anchor for items usually
    itemSprite.anchor.set(0.5, 1);

    // Calculate position based on HoldPoint
    const holdPoint = this.spritesheetService.getHoldPointForFrame(
      itemSpritesheet.spritesheetId,
      1,
    );
    if (holdPoint) {
      const baseX = slot === 'offHand' ? -holdPoint.x : holdPoint.x;
      itemSprite.position.set(baseX, -holdPoint.y);
    } else {
      // Fallback offsets
      if (slot === 'mainHand') itemSprite.position.set(6, -8);
      else if (slot === 'offHand') itemSprite.position.set(-6, -8);
      else itemSprite.position.set(0, 0);
    }

    // Inherit animation offsets from parent
    const animOffset = world.getComponent(ownerId, 'AnimationTransformOffset');
    if (animOffset) {
      itemSprite.x += animOffset.x;
      itemSprite.y += animOffset.y;
      itemSprite.rotation = animOffset.rotation;
      itemSprite.scale.set(animOffset.scaleX, animOffset.scaleY);
    }
    // Note: We specifically avoid inheriting the owner's Sprite.tint here,
    // as it often contains skin colour which shouldn't affect equipment.
  }
}
